/**
 * useRealtime — Conexión SSE al backend para refrescar módulos cuando cambia el
 * estado del restaurante en otra máquina (mesero crea pedido → cocina ve la lista
 * actualizada sin recargar).
 *
 * Ejemplo:
 *   useRealtime(['pedido_creado', 'pedido_actualizado'], () => cargar());
 *
 * El handler se llama con el evento decodificado. Si solo quieres recargar
 * pasa una función sin argumentos.
 *
 * ---------------------------------------------------------------------------
 * UNA SOLA CONEXIÓN POR PESTAÑA (oct-2026)
 *
 * Antes cada llamada a useRealtime abría su PROPIO EventSource. Una pestaña con
 * el POS abierto tenía 4 o 5 conexiones SSE vivas (notificaciones, badge de
 * aprobaciones, mesas, el módulo…). El navegador permite 6 conexiones HTTP/1.1
 * por host, compartidas entre TODAS las pestañas: con dos o tres pestañas de
 * Salonika abiertas, las SSE se comían el cupo y cualquier otra petición a la
 * API se quedaba colgada para siempre (la app "se congela" sin error). Se
 * reprodujo el 9-oct-2026: localhost:4000 no respondía desde el navegador y
 * 127.0.0.1:4000 (otro cupo) respondía al instante.
 *
 * Ahora hay un único EventSource compartido; cada hook solo registra sus
 * handlers. Se abre con el primer suscriptor y se cierra con el último.
 * ---------------------------------------------------------------------------
 */
import { useEffect, useRef } from 'react';
import { apiBaseUrl, tokenStore } from '../../api/client';
import type { EstadoTiempoReal } from '../../lib/conexion';

export type RealtimeEvent = {
  type: string;
  data: any;
};

type Handler = (event: RealtimeEvent) => void;

// ---- Conexión compartida (módulo, no React) --------------------------------
const suscriptores = new Map<string, Set<Handler>>();   // tipo de evento → handlers
let es: EventSource | null = null;
let tokenDeLaConexion: string | null = null;
let tiposEscuchados = new Set<string>();                 // tipos ya enganchados al `es` actual
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let cierreTimer: ReturnType<typeof setTimeout> | null = null;

// ---- Estado de la conexión (lo lee el indicador de conexión, oct-2026) -----
// Si el servidor responde pero esta conexión está caída, la pantalla deja de
// recibir los pedidos de otros equipos sin que nada lo diga. El indicador lo
// muestra como "Sin actualizaciones en vivo". Ver app/lib/monitor-conexion.ts.
let estadoTR: { estado: EstadoTiempoReal; desde: number } = { estado: 'sin_uso', desde: Date.now() };
const oyentesTR = new Set<() => void>();

function fijarEstadoTR(estado: EstadoTiempoReal) {
  if (estadoTR.estado === estado) return;
  // De caído a reconectando sigue siendo la misma caída: se conserva desde
  // cuándo, o cada reintento (cada 3 s) reiniciaría el margen y el indicador
  // parpadearía entre verde y ámbar.
  const mismaCaida = estado === 'conectando' && (estadoTR.estado === 'caido' || estadoTR.estado === 'conectando');
  estadoTR = { estado, desde: mismaCaida ? estadoTR.desde : Date.now() };
  for (const f of Array.from(oyentesTR)) {
    try { f(); } catch { /* un oyente no debe romper el stream */ }
  }
}

export function getEstadoTiempoReal() { return estadoTR; }

export function onEstadoTiempoReal(fn: () => void): () => void {
  oyentesTR.add(fn);
  return () => { oyentesTR.delete(fn); };
}

function despachar(type: string, ev: MessageEvent) {
  let data: any;
  try { data = ev.data ? JSON.parse(ev.data) : null; } catch { data = ev.data; }
  for (const h of Array.from(suscriptores.get(type) || [])) {
    try { h({ type, data }); } catch (err) { console.error('[realtime] handler falló:', err); }
  }
}

function escuchar(type: string) {
  if (!es || tiposEscuchados.has(type)) return;
  es.addEventListener(type, (ev) => despachar(type, ev as MessageEvent));
  tiposEscuchados.add(type);
}

function cerrarConexion(motivo: 'fin' | 'reabrir' = 'fin') {
  if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
  if (es) es.close();
  es = null;
  tokenDeLaConexion = null;
  tiposEscuchados = new Set();
  // Al reabrir no se pasa por 'sin_uso': borraría el registro de la caída.
  if (motivo === 'fin') fijarEstadoTR('sin_uso');
}

function asegurarConexion() {
  const token = tokenStore.get();
  if (!token) return; // sin sesión no hay stream
  // Ya abierta con la misma sesión: solo faltan los tipos nuevos.
  if (es && tokenDeLaConexion === token && es.readyState !== EventSource.CLOSED) {
    for (const t of suscriptores.keys()) escuchar(t);
    return;
  }
  cerrarConexion('reabrir');
  tokenDeLaConexion = token;
  es = new EventSource(`${apiBaseUrl}/api/events?token=${encodeURIComponent(token)}`);
  fijarEstadoTR('conectando');
  for (const t of suscriptores.keys()) escuchar(t);
  es.onopen = () => fijarEstadoTR('abierto');
  es.onerror = () => {
    // Reconectando solo (CONNECTING) o muerto (CLOSED): en los dos casos ya
    // no llegan eventos. El indicador da un margen antes de avisar.
    fijarEstadoTR(es && es.readyState === EventSource.CLOSED ? 'caido' : 'conectando');
    // EventSource intenta reconectar solo, pero a veces queda en CLOSED.
    // Forzamos reconexión después de 3s si todavía hay quien escuche.
    if (es && es.readyState === EventSource.CLOSED) {
      es.close();
      es = null;
      tiposEscuchados = new Set();
      if (reconnectTimer) clearTimeout(reconnectTimer);
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        if (suscriptores.size) asegurarConexion();
      }, 3000);
    }
  };
}

function suscribir(tipos: string[], h: Handler): () => void {
  if (cierreTimer) { clearTimeout(cierreTimer); cierreTimer = null; }
  for (const t of tipos) {
    if (!suscriptores.has(t)) suscriptores.set(t, new Set());
    suscriptores.get(t)!.add(h);
  }
  asegurarConexion();
  return () => {
    for (const t of tipos) {
      const set = suscriptores.get(t);
      if (!set) continue;
      set.delete(h);
      if (!set.size) suscriptores.delete(t);
    }
    // Sin nadie escuchando, se cierra. Con un respiro: al navegar entre
    // módulos uno se desmonta y otro se monta en el mismo tick; cerrar y
    // reabrir ahí sería una reconexión por cada clic en el menú.
    if (!suscriptores.size && !cierreTimer) {
      cierreTimer = setTimeout(() => {
        cierreTimer = null;
        if (!suscriptores.size) cerrarConexion();
      }, 2000);
    }
  };
}

export function useRealtime(eventos: string[], handler: Handler) {
  // Mantenemos el handler en un ref para que el effect no se re-ejecute cuando
  // el componente re-renderiza (típico cuando el handler se define inline).
  const handlerRef = useRef(handler);
  useEffect(() => { handlerRef.current = handler; }, [handler]);

  // Memoizamos la lista de eventos por su contenido (no por la referencia del array)
  const eventosKey = eventos.join(',');

  useEffect(() => {
    if (!tokenStore.get()) return; // sin sesión no hay stream
    const h: Handler = (ev) => handlerRef.current(ev);
    return suscribir(eventosKey.split(',').filter(Boolean), h);
  }, [eventosKey]);
}
